import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Input,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Box
} from '@chakra-ui/react';
import { ContentModerationService } from '../services/moderationService';
import { useAuth } from '../contexts/AuthContext';

interface ReportUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportedUserId: string;
  reportedUserName: string;
}

const ReportUserModal: React.FC<ReportUserModalProps> = ({
  isOpen,
  onClose,
  reportedUserId,
  reportedUserName
}) => {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    reportType: '',
    description: '',
    evidence: ''
  });

  const reportTypes = [
    { value: 'harassment', label: 'Harassment', color: 'red' },
    { value: 'spam', label: 'Spam Account', color: 'orange' },
    { value: 'fake_account', label: 'Fake Account', color: 'purple' },
    { value: 'inappropriate_behavior', label: 'Inappropriate Behavior', color: 'red' },
    { value: 'other', label: 'Other', color: 'gray' }
  ];

  const handleSubmit = async () => {
    if (!user || !userProfile) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to submit a report.',
        status: 'error'
      });
      return;
    }

    if (!formData.reportType || !formData.description.trim()) {
      toast({
        title: 'Missing information',
        description: 'Please select a report type and provide a description.',
        status: 'error'
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      const evidence = formData.evidence.trim() ? [formData.evidence.trim()] : [];
      
      await ContentModerationService.reportUser(
        user.uid,
        userProfile.name || user.displayName || 'Anonymous',
        reportedUserId,
        reportedUserName,
        formData.reportType as any,
        formData.description.trim(),
        evidence
      );

      toast({
        title: 'Report submitted successfully',
        description: 'Thank you for helping keep our community safe. We will review your report.',
        status: 'success',
        duration: 5000
      });

      // Reset form
      setFormData({ reportType: '', description: '', evidence: '' });
      onClose();
    } catch (error: any) {
      console.error('Error submitting report:', error);
      toast({
        title: 'Failed to submit report',
        description: error.message || 'An error occurred. Please try again.',
        status: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedReportType = reportTypes.find(rt => rt.value === formData.reportType);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Report User</ModalHeader>
        <ModalBody>
          <VStack gap={4} align="stretch">
            {/* User Info */}
            <Box p={4} bg="gray.700" borderRadius="md">
              <Text fontSize="sm" color="gray.400">Reporting user:</Text>
              <Text fontWeight="bold">{reportedUserName}</Text>
            </Box>

            {/* Report Type */}
            <FormControl isRequired>
              <FormLabel>What is the issue?</FormLabel>
              <Select
                placeholder="Select a report type"
                bg="gray.700"
                border="none"
                color="white"
                _focus={{ bg: 'gray.600' }}
                value={formData.reportType}
                onChange={(e) => setFormData(prev => ({ ...prev, reportType: e.target.value }))}
              >
                {reportTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Selected Report Type Info */}
            {selectedReportType && (
              <Alert status="info" bg="blue.900" color="blue.100">
                <AlertIcon />
                <AlertDescription>
                  <Text fontSize="sm">
                    {selectedReportType.value === 'harassment' && 
                      'Please provide specific details about the harassment you experienced, including when and where it occurred.'
                    }
                    {selectedReportType.value === 'spam' && 
                      'Please explain why you believe this account is being used for spam purposes.'
                    }
                    {selectedReportType.value === 'fake_account' && 
                      'Please provide evidence that this account is fake or impersonating someone else.'
                    }
                    {selectedReportType.value === 'inappropriate_behavior' && 
                      'Please describe the inappropriate behavior you observed.'
                    }
                    {selectedReportType.value === 'other' && 
                      'Please provide additional details about the issue.'
                    }
                  </Text>
                </AlertDescription>
              </Alert>
            )}

            {/* Description */}
            <FormControl isRequired>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="Please provide a detailed description of the issue..."
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
              />
            </FormControl>

            {/* Evidence */}
            <FormControl>
              <FormLabel>Evidence (Optional)</FormLabel>
              <Input
                placeholder="URL to screenshot or other evidence"
                bg="gray.700"
                border="none"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ bg: 'gray.600' }}
                value={formData.evidence}
                onChange={(e) => setFormData(prev => ({ ...prev, evidence: e.target.value }))}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                You can provide links to screenshots, messages, or other evidence that supports your report.
              </Text>
            </FormControl>

            {/* Warning */}
            <Alert status="warning" bg="yellow.900" color="yellow.100">
              <AlertIcon />
              <AlertDescription>
                <Text fontSize="sm">
                  <strong>Important:</strong> False reports may result in account restrictions. 
                  Please ensure your report is accurate and made in good faith.
                </Text>
              </AlertDescription>
            </Alert>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Submitting Report"
            isDisabled={!formData.reportType || !formData.description.trim()}
          >
            Submit Report
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ReportUserModal;
